import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Panel, Button, Badge } from "@realtyflows/ui";
import {
  getRoleCatalog,
  getPermissionCatalog,
  getMyPermissions,
} from "../api";

/**
 * RolesPermissions — V5 Feature 5.8 UI
 *
 * Shows the system role catalog (what each role can do) and the
 * current user's resolved permissions with their sources (which role
 * granted which permission).
 *
 * Assigning roles to other users is handled via the Team page menu
 * (when on a team) — this page is the read-only reference + audit view.
 */
export default function RolesPermissions() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getRoleCatalog(), getPermissionCatalog(), getMyPermissions()])
      .then(([r, p, m]) => { setRoles(r); setPermissions(p); setMine(m); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontFamily: font.body }}>Loading...</div>;
  }

  const groupedPermissions = permissions.reduce((acc, perm) => {
    const [resource] = perm.split(".");
    if (!acc[resource]) acc[resource] = [];
    acc[resource].push(perm);
    return acc;
  }, {});

  const myPermSet = new Set(mine?.permissions || []);
  const hasPermission = (p) =>
    myPermSet.has(p) || myPermSet.has("*") || myPermSet.has(`${p.split(".")[0]}.*`) || myPermSet.has(`*.${p.split(".")[1]}`);

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => navigate("/settings")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
          ← Back to Settings
        </button>
      </div>

      <Panel padding={24} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>
          Roles & Permissions
        </div>
        <div style={{ fontSize: 13, color: t.textMuted, marginTop: 4, fontFamily: font.body }}>
          What each role can do, and what you personally have access to.
        </div>
      </Panel>

      {/* My permissions */}
      {mine && (
        <Panel padding={20} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
            Your Access
          </div>
          {mine.contexts.length === 0 ? (
            <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic", fontFamily: font.body }}>
              You have no roles assigned. You're a basic user.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {mine.contexts.map((ctx, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: 8, background: t.surfaceWarm,
                  border: `1px solid ${t.border}`, borderRadius: 6,
                }}>
                  <Badge color={t.emerald}>{ctx.role}</Badge>
                  <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono }}>
                    scope: {ctx.scope}{ctx.scopeId != null ? ` #${ctx.scopeId}` : ""}
                  </span>
                  {ctx.source && (
                    <span style={{ fontSize: 10, color: t.textMuted, marginLeft: "auto", fontFamily: font.mono }}>
                      via {ctx.source}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 14, fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
            <strong>Total permissions:</strong> {mine.permissions.length === 1 && mine.permissions[0] === "*"
              ? "All (super admin)"
              : `${mine.permissions.length} permissions`}
          </div>
        </Panel>
      )}

      {/* Role catalog */}
      <Panel padding={20} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
          Role Catalog
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {roles.map((role) => (
            <div key={role.slug} style={{
              padding: 12, background: t.surfaceWarm,
              border: `1px solid ${t.border}`, borderRadius: 8,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
                {role.label} <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, fontWeight: 400, marginLeft: 6 }}>{role.slug}</span>
              </div>
              <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
                {role.permissions.map((p) => (
                  <span key={p} style={{
                    fontSize: 10, padding: "2px 6px", borderRadius: 4,
                    background: t.surface, border: `1px solid ${t.border}`,
                    fontFamily: font.mono, color: t.textSecondary,
                  }}>{p}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Permission grid */}
      <Panel padding={20}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
          All Permissions (✓ = you have it)
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
          {Object.entries(groupedPermissions).map(([resource, perms]) => (
            <div key={resource} style={{
              padding: 10, background: t.surfaceWarm,
              border: `1px solid ${t.border}`, borderRadius: 8,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, fontFamily: font.mono, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                {resource}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {perms.map((p) => {
                  const have = hasPermission(p);
                  return (
                    <div key={p} style={{
                      display: "flex", alignItems: "center", gap: 6,
                      fontSize: 11, fontFamily: font.mono,
                      color: have ? t.emerald : t.textMuted,
                    }}>
                      <span style={{ fontWeight: 700 }}>{have ? "✓" : "○"}</span>
                      <span>{p}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
